import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useEvents from './hooks/useEvents';
import moment from 'moment-timezone';
import { Divider } from '@/src/tailwindui/divider';
import { Badge } from '@/src/tailwindui/badge';
import {
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownMenu,
} from '@/src/tailwindui/dropdown';
import { EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import SmallLoader from '@/src/Common/components/SmallLoader';
import { ExternalLinkIcon, TrashIcon } from 'lucide-react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { toast } from 'sonner';
export default function EventRow({
    event,
    index,
}: {
    event: any;
    index: number;
}) {
    const authenticatedApi = useAuthenticatedApi();
    const navigate = useNavigate();
    const { getActivationPhotoStats } = useEvents();
    const [photoStats, setPhotoStats] = useState<any>(null);
    const isToday = moment(event.date).isSame(moment(), 'day');
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const { orgId } = useUserOrg();

    useEffect(() => {
        let isMounted = true;
        const fetchStats = async () => {
            setIsLoadingStats(true);
            const stats = await getActivationPhotoStats(event.id);
            if (isMounted) {
                setPhotoStats(stats);
                setIsLoadingStats(false);
            }
        };
        fetchStats();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event.id]);

    const aspectRatio = photoStats?.lastPhoto?.aspect_ratio?.toFixed(2);

    const aspectRatioLookup = {
        '1.7': 'aspect-[16/9]',
        '1.5': 'aspect-3/2',
        '1.3': 'aspect-4/3',
        '1': 'aspect-1/1',
        '0.75': 'aspect-4/3',
        '0.66': 'aspect-3/2',
        '0.56': 'aspect-[16/9]',
    };

    const aspectRatioClass =
        // @ts-ignore
        aspectRatioLookup[String(aspectRatio)] || 'aspect-3/2';

    return (
        <>
            <li key={event.id}>
                <Divider soft={index > 0} />
                <div className="flex items-center justify-between">
                    <div key={event.id} className="flex gap-6 py-6">
                        <div className="w-16 md:w-48 shrink-0">
                            <Link
                                to={`/events/${event.id}`}
                                // href={event.url}
                                aria-hidden="true"
                            >
                                <img
                                    className={`rounded-lg shadow-sm object-cover shadow-zinc-950/5 dark:shadow-white/10 ${aspectRatioClass}`}
                                    src={
                                        photoStats?.lastPhoto
                                            ? photoStats?.lastPhoto
                                                  ?.thumbnail_uri
                                            : '/images/placeholder.png'
                                    }
                                    alt=""
                                />
                            </Link>
                        </div>
                        <div
                            className="space-y-1.5 cursor-pointer"
                            onClick={() => {
                                navigate(`/events/${event.id}`);
                            }}
                        >
                            <div className="text-base/6 monospace-font text-black dark:text-white font-semibold">
                                <Link
                                    to={`/events/${event.id}`}
                                    //href={event.url}
                                >
                                    {event.name}
                                </Link>
                            </div>
                            <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                {isToday
                                    ? 'Today'
                                    : moment(event.date).format('MMM Do, YYYY')}
                            </div>
                            <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                {event.location}
                            </div>

                            <div className="text-sm text-zinc-600 dark:text-zinc-400">
                                {isLoadingStats ? (
                                    <SmallLoader />
                                ) : photoStats?.photoCount &&
                                  photoStats?.photoCount > 0 ? (
                                    <span className="text-sm text-zinc-600 dark:text-zinc-400">
                                        <b>{photoStats?.photoCount}</b> total
                                        photos
                                    </span>
                                ) : (
                                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                        No photos yet.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <Badge
                            className="hidden lg:block"
                            color={isToday ? 'green' : 'zinc'}
                        >
                            {isToday ? 'Today' : moment(event.date).fromNow()}
                        </Badge>
                        <Dropdown>
                            <DropdownButton plain aria-label="More options">
                                <EllipsisVerticalIcon className="text-zinc-500 dark:text-white w-5 h-5" />
                            </DropdownButton>
                            <DropdownMenu anchor="bottom end">
                                <DropdownItem
                                    onClick={() => {
                                        const deleteRequest = async () => {
                                            const response =
                                                await authenticatedApi.put(
                                                    `${orgId}/activations/${event.id}/archive`,
                                                );

                                            if (response.status === 200) {
                                                navigate('/events');
                                            }
                                            toast.success('Event archived');
                                        };

                                        const shouldDelete = window.confirm(
                                            `Are you sure you would like to archive ${event.name}`,
                                        );

                                        if (shouldDelete) {
                                            deleteRequest();
                                        }
                                    }}
                                >
                                    Archive{' '}
                                    <TrashIcon className="ml-2 w-4 h-4" />
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() => {
                                        window.open(
                                            `https://gallery.photoengineers.com/gallery/${
                                                photoStats?.slug ??
                                                photoStats?.galleryId
                                            }`,
                                            '_blank',
                                        );
                                    }}
                                >
                                    Gallery{' '}
                                    <ExternalLinkIcon
                                        className="ml-2 w-4 h-4"
                                        strokeWidth={1.5}
                                    />
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                </div>
            </li>
        </>
    );
}
